import { Flex, Group, TextInput } from '@mantine/core'
import { IconLanguage, IconX } from '@tabler/icons-react'
import cx from 'classnames'
import React from 'react'
import { ServerError } from '#/api/ServerError'
import { ActionEnum } from '#/api/models/actionEnum'
import type { AdvancedFeatureResponse } from '#/api/models/advancedFeatureResponse'
import type { DataResponse } from '#/api/models/dataResponse'
import { getLanguagesRetrieveQueryKey, useLanguagesRetrieve } from '#/api/react-query/other'
import {
  useAssetsAdvancedFeaturesCreate,
  useAssetsAdvancedFeaturesPartialUpdate,
  useAssetsDataSupplementPartialUpdate,
} from '#/api/react-query/survey-data'
import ActionIcon from '#/components/common/ActionIcon'
import KoboIcon from '#/components/common/KoboIcon'
import Alert from '#/components/common/alert'
import Button from '#/components/common/button'
import LoadingSpinner from '#/components/common/loadingSpinner'
import type { LanguageCode } from '#/components/languages/languagesStore'
import ConflictingOngoingJobAlert from '#/components/processing/common/ConflictingOngoingJobAlert'
import { SUBSEQUENCES_SCHEMA_VERSION } from '#/components/processing/common/constants'
import { getLatestAutomaticTranslationVersionItem } from '#/components/processing/common/utils'
import { ProcessingTab, goToProcessing } from '#/components/processing/routes.utils'
import type { AssetResponse } from '#/dataInterface'
import { getSubmissionRootUuid, notify } from '#/utils'
import bodyStyles from '../../../common/processingBody.module.scss'

interface Props {
  asset: AssetResponse
  questionXpath: string
  languageCode: LanguageCode
  submission: DataResponse
  hasConflictingOngoingJob: boolean
  onBack: () => void
  onLimitExceeded: () => void
  onCreate: (languageCode: LanguageCode, context: 'automated' | 'manual') => void
  advancedFeatures: AdvancedFeatureResponse[]
}

export default function StepCreateAutomated({
  asset,
  questionXpath,
  languageCode,
  submission,
  hasConflictingOngoingJob,
  onBack,
  onLimitExceeded,
  onCreate,
  advancedFeatures,
}: Props) {
  const advancedFeature = advancedFeatures.find(
    (af) => af.action === ActionEnum.automatic_google_translation && af.question_xpath === questionXpath,
  )

  const mutationCreateAF = useAssetsAdvancedFeaturesCreate()
  const mutationPatchAF = useAssetsAdvancedFeaturesPartialUpdate()
  const mutationCreateAutomaticTranslation = useAssetsDataSupplementPartialUpdate({
    mutation: {
      onSuccess(response, _variables, _context) {
        if (response.status !== 200) return // just a typeguard, shouldn't happen in `onSuccess`.
        const translationVersion = getLatestAutomaticTranslationVersionItem(response.data, questionXpath, languageCode)
        if (
          translationVersion?._data &&
          'status' in translationVersion?._data &&
          translationVersion?._data.status === 'failed'
        ) {
          notify(translationVersion?._data.error, 'error', {}, translationVersion?._data.error)
        }
      },
      onError: (err) => {
        if (err instanceof ServerError && err.response.status === 402) {
          onLimitExceeded()
        } else {
          notify.error(t('Failed to create automatic translation. Please try again later.'))
        }
      },
    },
  })

  // TODO: HACK-FIX: We should rely on passing Language instead of LanguageCode throughout the single processing view to avoid
  // using the languages hook here, but this involves dealing with time consuming type handling for LanguageSelector.
  const { data, isLoading: isLoadingLanguages } = useLanguagesRetrieve(languageCode, {
    query: {
      queryKey: getLanguagesRetrieveQueryKey(languageCode),
      enabled: languageCode !== '',
    },
  })
  const language = data?.status === 200 ? data.data : undefined

  const latestAutomaticTranslation =
    mutationCreateAutomaticTranslation.data?.status === 200
      ? getLatestAutomaticTranslationVersionItem(
          mutationCreateAutomaticTranslation.data?.data,
          questionXpath,
          languageCode,
        )?._data
      : undefined
  const errorMessage =
    latestAutomaticTranslation &&
    'status' in latestAutomaticTranslation &&
    latestAutomaticTranslation.status === 'failed' &&
    latestAutomaticTranslation.error

  const anyPending =
    mutationCreateAF.isPending ||
    mutationPatchAF.isPending ||
    mutationCreateAutomaticTranslation.isPending ||
    isLoadingLanguages

  function handleClickBack() {
    onBack()
  }

  async function handleCreateTranslation() {
    // Keep a runtime guard in addition to disabled UI controls.
    // This protects against stale state and accidental double-trigger paths.
    if (hasConflictingOngoingJob) return

    // Silently under the hook enable advanced features if needed.
    if (!advancedFeature) {
      await mutationCreateAF.mutateAsync({
        uidAsset: asset.uid,
        data: {
          question_xpath: questionXpath,
          action: ActionEnum.automatic_google_translation,
          params: [
            {
              language: languageCode,
            },
          ],
        },
      })
    } else if (!advancedFeature?.params.find((param) => 'language' in param && param.language === languageCode)) {
      await mutationPatchAF.mutateAsync({
        uidAsset: asset.uid,
        uidAdvancedFeature: advancedFeature.uid,
        data: {
          action: ActionEnum.automatic_google_translation,
          question_xpath: questionXpath,
          params: advancedFeature.params.concat({
            language: languageCode,
          }),
        } as any,
      })
    }

    try {
      const response = await mutationCreateAutomaticTranslation.mutateAsync({
        uidAsset: asset.uid,
        rootUuid: getSubmissionRootUuid(submission),
        data: {
          _version: SUBSEQUENCES_SCHEMA_VERSION,
          [questionXpath]: {
            automatic_google_translation: { language: languageCode },
          },
        },
      })

      // This endpoint is expected to come back with 200 on success. If that ever changes,
      // the API contract needs fixing instead of guessing our way through it here.
      if (response.status !== 200) return

      const translationVersion = getLatestAutomaticTranslationVersionItem(response.data, questionXpath, languageCode)
      if (
        translationVersion?._data &&
        'status' in translationVersion._data &&
        (translationVersion._data.status === 'failed' || translationVersion._data.status === 'in_progress')
      ) {
        if (translationVersion._data.status === 'in_progress') {
          const submissionEditId = getSubmissionRootUuid(submission)
          goToProcessing(asset.uid, questionXpath, submissionEditId, ProcessingTab.Translations, languageCode)
        }
        return
      }

      onCreate(languageCode, 'automated')
    } catch {
      // Error is handled by the onError callback above
      return
    }
  }

  if (!languageCode) return null

  if (mutationCreateAutomaticTranslation.isPending) {
    return (
      <div className={cx(bodyStyles.root, bodyStyles.stepConfig)}>
        <LoadingSpinner type='big' message={false} />

        <header className={bodyStyles.header}>{t('Automatic translation in progress')}</header>

        {/*
          Automatic translation is much faster than automatic transcription, but
          for the consistency sake we use similar UI here.
        */}
        <p>{t('Estimated time for completion: ##estimate##').replace('##estimate##', t('less than a minute'))}</p>
      </div>
    )
  }

  return (
    <div className={cx(bodyStyles.root, bodyStyles.stepConfig)}>
      <header className={bodyStyles.header}>{t('Automatic translation of transcript to')}</header>

      <Flex component='section' direction='row' align='center' justify='center' mb='xxl'>
        <Group gap='xs'>
          <TextInput
            readOnly
            value={language?.name || ''}
            leftSection={<KoboIcon icon={IconLanguage} size='sm' />}
            w={220}
            size='sm'
            rightSection={
              <ActionIcon
                disabled={anyPending}
                aria-label={t('Close')}
                variant='transparent'
                size='sm'
                onClick={handleClickBack}
                icon={IconX}
              />
            }
          />
        </Group>
      </Flex>

      <h2>{t('Translation provider')}</h2>

      <p>
        {t(
          'Automated translation is provided by Google Cloud Platform. By using ' +
            'this service you agree that your transcript text will be sent to ' +
            "Google's servers for the purpose of translation. However, it will not " +
            "be stored on Google's servers beyond the very short period needed for " +
            'completing the translation.',
        )}
      </p>

      {errorMessage && (
        <div>
          <Alert iconName='alert' type='error' mt='md'>
            {errorMessage}
          </Alert>
        </div>
      )}

      {hasConflictingOngoingJob && <ConflictingOngoingJobAlert mt='md' />}

      <footer className={bodyStyles.footer}>
        <div className={bodyStyles.footerCenterButtons}>
          <Button type='secondary' size='m' label={t('cancel')} onClick={handleClickBack} isDisabled={anyPending} />

          <Button
            type='primary'
            size='m'
            label={t('create translation')}
            onClick={handleCreateTranslation}
            isDisabled={anyPending || hasConflictingOngoingJob}
          />
        </div>
      </footer>
    </div>
  )
}
